import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ReviewCommentsCallout from '@/components/review/ReviewCommentsCallout';
import { useReviewCommentsIndex } from '@/hooks/useReviewCommentsIndex';
import type { ThreatCatNote } from '@/types/note';

const NOTE_TYPE_LABELS: Record<ThreatCatNote['type'], string> = {
  general: 'General',
  assumption: 'Assumption',
  scope: 'In Scope',
  out_of_scope: 'Out of Scope',
};

const Link = ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
);
const mdComponents = { a: Link };

interface DiagramNotesTabProps {
  notes: ThreatCatNote[];
}

export default function DiagramNotesTab({ notes }: DiagramNotesTabProps) {
  const reviewIndex = useReviewCommentsIndex();

  if (notes.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground italic">No notes for this diagram.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {notes.map((note) => {
        const noteComments =
          typeof note.id === 'number'
            ? reviewIndex.getNoteCommentsById(note.id)
            : reviewIndex.getNoteComments(note.diagram_id ?? null, note.type);
        return (
          <Card key={note.uuid ?? note.id ?? note.type}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {NOTE_TYPE_LABELS[note.type] ?? note.type}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ReviewCommentsCallout comments={noteComments} />
              <div className="prose prose-sm dark:prose-invert max-w-none prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-table:rounded-lg prose-td:px-3 prose-th:px-3">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                  components={mdComponents}
                >
                  {note.value}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
